import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * Layout-matching placeholder.
 *
 * The heading and intro stay rendered — only the rows, whose count and content
 * are genuinely unknown, are skeletoned. An empty list while the query is in
 * flight would read as "no changes have ever been recorded", which is a
 * different and much more alarming statement.
 */
export const RateHistorySkeleton: React.FC = () => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('settings.history.heading')}
            className="flex w-full flex-col gap-4"
            {...getNodeTestIdProp('settings-history-skeleton')}
        >
            <div className="flex flex-col gap-1">
                <h2 className="text-sm font-semibold text-foreground">
                    {t('settings.history.heading')}
                </h2>
                <p className="text-xs text-muted">
                    {t('settings.history.intro')}
                </p>
            </div>
            <div className="flex flex-col gap-3">
                {[0, 1].map((row) => (
                    <div
                        className="flex flex-col gap-3 rounded-xl bg-surface-secondary p-4"
                        key={row}
                    >
                        <Skeleton className="h-5 w-40 rounded" />
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                            {[0, 1, 2, 3].map((cell) => (
                                <Skeleton
                                    className="h-8 w-full rounded"
                                    key={cell}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};
